/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { useState } from 'react'
import { cn } from 'app/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

type Side = 'top' | 'right' | 'bottom' | 'left'
type Align = 'start' | 'center' | 'end'

type Placement = `${Side}` | `${Side}-${Align}`

interface TooltipWrapperProps {
  children: React.ReactNode
  message?: React.ReactNode
  placement?: Placement
  keepOpen?: boolean
  // When true, clicks on the trigger don't dismiss the tooltip. Use for disabled
  // buttons whose tooltip explains *why* they're disabled — closing on click would
  // hide the only feedback the user gets.
  pinOnClick?: boolean
  /**
   * Hover delay before opening. Leave unset for the instant default; set it on
   * a repeated element (a table column) so sweeping the pointer down a list
   * does not fire a tooltip per row.
   */
  delayDuration?: number
  onMouseOver?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void
  className?: string
  tooltipClassName?: string
}

function mapPlacement(placement?: Placement): { side: Side; align: Align } {
  const [side, align] = (placement ?? 'bottom').split('-')
  return {
    side: (side as Side) ?? 'bottom',
    align: (align as Align) ?? 'center',
  }
}

export function TooltipWrapper({
  children,
  message,
  placement,
  keepOpen,
  pinOnClick,
  delayDuration,
  onMouseOver,
  className,
  tooltipClassName,
}: TooltipWrapperProps) {
  const { side, align } = mapPlacement(placement)

  // When pinOnClick is set, drive the tooltip's open state ourselves from
  // hover/focus events and intentionally do NOT pass onOpenChange — Radix's
  // internal close-on-click logic calls onOpenChange(false), so omitting that
  // prop makes the tooltip ignore close requests and stay open until the user
  // actually moves their cursor off the trigger.
  const [pinnedOpen, setPinnedOpen] = useState(false)
  const tooltipProps = pinOnClick ? { open: pinnedOpen, delayDuration: delayDuration ?? 200 } : {}
  const triggerHoverProps = pinOnClick
    ? {
        onPointerEnter: () => setPinnedOpen(true),
        onPointerLeave: () => setPinnedOpen(false),
        onFocus: () => setPinnedOpen(true),
        onBlur: () => setPinnedOpen(false),
      }
    : {}

  return (
    <TooltipProvider disableHoverableContent={!keepOpen} delayDuration={delayDuration}>
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>
          <span
            x-component-name="Tooltip"
            className={cn('cursor-default', className)}
            onMouseOver={onMouseOver}
            {...triggerHoverProps}
          >
            {children}
          </span>
        </TooltipTrigger>
        {message && (
          <TooltipContent side={side} align={align} className={cn('tooltip-wrapper z-50', tooltipClassName)}>
            {message}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  )
}
